<template>
  <div class="ctx-tab">
    <section class="ctx-drawer__card" aria-labelledby="ctx-fields-heading">
      <div class="ctx-drawer__card-head">
        <span class="ctx-drawer__card-icon" aria-hidden="true">
          <v-icon icon="mdi-form-textbox" size="18" />
        </span>
        <h3 id="ctx-fields-heading" class="ctx-drawer__card-title">
          Fields
          <span class="ctx-drawer__card-count">{{ customFields.length }}</span>
        </h3>
      </div>

      <ul v-if="customFields.length" class="ctx-drawer__fields">
        <li
          v-for="field in customFields"
          :key="field.key"
          class="ctx-drawer__field"
          :class="{ 'ctx-drawer__field--disabled': !field.enabled }"
        >
          <span class="ctx-drawer__field-icon" aria-hidden="true">
            <v-icon :icon="field.icon" size="16" />
          </span>
          <div class="ctx-drawer__field-copy">
            <span class="ctx-drawer__field-name">
              {{ field.label }}
              <span v-if="field.isSystem" class="ctx-drawer__field-badge">
                default
              </span>
              <span
                v-if="field.isUniqueIdentifier"
                class="ctx-drawer__field-badge ctx-drawer__field-badge--id"
              >
                identifier
              </span>
              <span
                v-if="field.isInternal"
                class="ctx-drawer__field-badge ctx-drawer__field-badge--internal"
                title="Hidden from Add lead, Edit lead and lead tables -- still mappable from bulk upload"
              >
                internal
              </span>
            </span>
            <span class="ctx-drawer__field-type">
              {{ field.type }}<template v-if="field.options">
                · {{ field.options }}</template>
            </span>
          </div>
          <div class="ctx-drawer__field-actions">
            <button
              v-if="!field.isUniqueIdentifier"
              type="button"
              class="ctx-drawer__field-action"
              aria-label="Make unique identifier"
              title="Leads are matched on this field (upsert)"
              :disabled="savingField || removingField || !field.enabled"
              @click="makeIdentifier(field)"
            >
              <v-icon icon="mdi-key-outline" size="15" />
            </button>
            <button
              v-if="!field.isSystem"
              type="button"
              class="ctx-drawer__field-action"
              :class="{ 'ctx-drawer__field-action--active': field.isInternal }"
              :aria-label="field.isInternal ? 'Show on lead forms' : 'Hide from lead forms'"
              :title="field.isInternal
                ? 'System-managed -- click to show on Add lead / Edit lead again'
                : 'Hide from Add lead, Edit lead and lead tables (still mappable from bulk upload)'"
              :disabled="savingField || removingField"
              @click="toggleInternal(field)"
            >
              <v-icon :icon="field.isInternal ? 'mdi-eye-off-outline' : 'mdi-eye-outline'" size="15" />
            </button>
            <button
              type="button"
              class="ctx-drawer__field-delete"
              aria-label="Delete field"
              title="Delete field"
              :disabled="savingField || removingField"
              @click="askRemoveField(field)"
            >
              <v-icon icon="mdi-trash-can-outline" size="15" />
            </button>
          </div>
        </li>
      </ul>
      <p v-else class="ctx-drawer__empty">
        No fields registered yet. Add one below, or they are
        auto-registered from lead forms and CSV columns.
      </p>

      <div class="ctx-drawer__field-form">
        <span class="ctx-drawer__block-label">Add field</span>
        <div class="ctx-drawer__field-form-row">
          <v-text-field
            v-model="fieldForm.label"
            placeholder="Field label, e.g. Budget Range"
            variant="outlined"
            density="compact"
            hide-details
            class="ctx-drawer__field-input"
          />
          <v-select
            v-model="fieldForm.type"
            :items="CONTEXT_FIELD_TYPE_OPTIONS"
            variant="outlined"
            density="compact"
            hide-details
            :menu-props="{ zIndex: 4000 }"
            class="ctx-drawer__field-input ctx-drawer__field-input--type"
          />
        </div>
        <v-text-field
          v-if="fieldForm.type === 'DROPDOWN'"
          v-model="fieldForm.options"
          placeholder="Options, comma separated"
          variant="outlined"
          density="compact"
          hide-details
          class="ctx-drawer__field-input"
        />
        <div class="ctx-drawer__field-form-actions">
          <v-btn
            variant="flat"
            size="small"
            class="ctx-drawer__field-save"
            :loading="savingField"
            :disabled="!String(fieldForm.label).trim()"
            @click="saveFieldDefinition"
          >
            Add field
          </v-btn>
        </div>
      </div>
    </section>

    <v-dialog v-model="showRemoveConfirm" max-width="440" :z-index="4000" persistent>
      <div class="ctx-confirm">
        <div class="ctx-confirm__glow" aria-hidden="true"></div>

        <div class="ctx-confirm__head">
          <span class="ctx-confirm__icon" aria-hidden="true">
            <v-icon icon="mdi-trash-can-outline" size="22" />
          </span>
          <div class="ctx-confirm__head-copy">
            <span class="ctx-confirm__eyebrow">Remove field</span>
            <h3 class="ctx-confirm__title">Delete this field?</h3>
          </div>
          <button
            type="button"
            class="ctx-confirm__close"
            aria-label="Close"
            :disabled="removingField"
            @click="showRemoveConfirm = false"
          >
            <v-icon icon="mdi-close" size="18" />
          </button>
        </div>

        <div class="ctx-confirm__body">
          <div class="ctx-confirm__field-chip">
            <v-icon :icon="fieldToRemove?.icon || 'mdi-format-text'" size="16" />
            <span class="ctx-confirm__field-name">{{ fieldToRemove?.label }}</span>
            <span class="ctx-confirm__field-type">{{ fieldToRemove?.type }}</span>
          </div>
          <p class="ctx-confirm__text">
            This field will be removed from the product and will no longer
            appear on lead forms. Values already saved on existing leads are
            kept, but the field definition can't be restored.
          </p>
        </div>

        <div class="ctx-confirm__actions">
          <button
            type="button"
            class="ctx-confirm__btn ctx-confirm__btn--ghost"
            :disabled="removingField"
            @click="showRemoveConfirm = false"
          >
            Keep field
          </button>
          <button
            type="button"
            class="ctx-confirm__btn ctx-confirm__btn--danger"
            :disabled="removingField"
            @click="confirmRemoveField"
          >
            <v-progress-circular v-if="removingField" indeterminate size="14" width="2" />
            <v-icon v-else icon="mdi-trash-can-outline" size="16" />
            {{ removingField ? 'Deleting…' : 'Delete field' }}
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { addContextFields, removeContextFields } from '@/api/contextsApi'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import {
  normalizeContextFieldDefinitions,
  CONTEXT_FIELD_TYPE_OPTIONS,
} from '@/utils/contexts'
import '@/styles/contexts/info-drawer.css'

defineOptions({ name: 'ContextFormFieldsTab' })

const props = defineProps({
  context: { type: Object, default: null },
})

const emit = defineEmits(['saved', 'error'])

const fieldForm = ref({ label: '', type: 'TEXT', options: '' })
const savingField = ref(false)
const showRemoveConfirm = ref(false)
const fieldToRemove = ref(null)
const removingField = ref(false)

// Local override lets this tab consume addContextFields' response directly
// (it returns the full updated registry) instead of waiting on a parent
// refetch -- same pattern ContextInfoDrawer.vue used, kept local to this
// tab rather than shared with ContextDispositionsTab.vue (the two never
// cross-talk).
const definitionsOverride = ref(null)

const FIELD_TYPE_ICONS = {
  TEXT: 'mdi-format-text',
  NUMBER: 'mdi-numeric',
  DROPDOWN: 'mdi-form-dropdown',
  PHONE_NUMBER: 'mdi-phone-outline',
  DATE: 'mdi-calendar-outline',
  TIME: 'mdi-clock-outline',
  EMAIL: 'mdi-email-outline',
}

const customFields = computed(() => {
  const definitions = definitionsOverride.value ?? props.context?.custom_field_definitions
  if (!definitions || typeof definitions !== 'object') return []

  return Object.entries(definitions).map(([key, definition]) => {
    const type = String(definition?.type ?? 'TEXT').toUpperCase()
    const optionsList = Array.isArray(definition?.options)
      ? definition.options.filter(Boolean)
      : []
    return {
      key,
      label: String(definition?.label ?? '').trim() || key,
      type,
      icon: FIELD_TYPE_ICONS[type] || FIELD_TYPE_ICONS.TEXT,
      options: optionsList.join(', '),
      optionsList,
      enabled: definition?.enabled !== false,
      isSystem: Boolean(definition?.is_system),
      locked: Boolean(definition?.locked),
      isUniqueIdentifier: Boolean(definition?.is_unique_identifier),
      isInternal: Boolean(definition?.is_internal),
    }
  })
})

/**
 * addContextFields overwrites each named field's whole definition, so edits
 * must resend every row's full current state or omitted flags (like
 * is_unique_identifier) are silently cleared server-side. Rows are keyed by
 * label + explicit key (the map key from custom_field_definitions).
 */
function buildFullRows(overrides = {}) {
  return customFields.value.map((field) => {
    const override = overrides[field.key] || {}
    const type = override.type ?? field.type
    const row = {
      label: override.label ?? field.label,
      key: field.key,
      type,
      enabled: override.enabled ?? field.enabled,
      is_unique_identifier: override.is_unique_identifier ?? field.isUniqueIdentifier,
      is_internal: override.is_internal ?? field.isInternal,
    }
    const options = override.options ?? field.optionsList
    if (type === 'DROPDOWN' && options.length) {
      row.options = options
    }
    return row
  })
}

async function submitFieldRows(rows, successFallback) {
  savingField.value = true
  try {
    const response = await addContextFields({
      context_id: props.context?.context_id,
      field_definitions: rows,
    })
    if (!isApiSuccess(response)) {
      emit('error', getApiMessage(response, 'Failed to save field settings'))
      return false
    }

    const returned = response?.data?.custom_field_definitions
    if (returned && typeof returned === 'object') {
      definitionsOverride.value = normalizeContextFieldDefinitions(returned)
    }

    emit('saved', getApiMessage(response, successFallback))
    return true
  } finally {
    savingField.value = false
  }
}

async function makeIdentifier(field) {
  if (field.isUniqueIdentifier) return
  if (!field.enabled) {
    emit('error', 'Enable the field before making it the unique identifier.')
    return
  }
  const overrides = {}
  customFields.value.forEach((entry) => {
    overrides[entry.key] = { is_unique_identifier: entry.key === field.key }
  })
  await submitFieldRows(buildFullRows(overrides), `"${field.label}" is now the unique identifier.`)
}

async function toggleInternal(field) {
  const nextInternal = !field.isInternal
  const overrides = { [field.key]: { is_internal: nextInternal } }
  await submitFieldRows(
    buildFullRows(overrides),
    nextInternal
      ? `"${field.label}" is now hidden from Add lead, Edit lead and lead tables.`
      : `"${field.label}" is visible on lead forms again.`,
  )
}

function resetFieldForm() {
  fieldForm.value = { label: '', type: 'TEXT', options: '' }
}

function askRemoveField(field) {
  fieldToRemove.value = field
  showRemoveConfirm.value = true
}

async function confirmRemoveField() {
  if (!fieldToRemove.value) return

  removingField.value = true
  try {
    const removedKey = fieldToRemove.value.key
    const response = await removeContextFields({
      context_id: props.context?.context_id,
      field_names: [removedKey],
    })
    if (!isApiSuccess(response)) {
      emit('error', getApiMessage(response, 'Failed to delete field'))
      return
    }

    const current =
      definitionsOverride.value ??
      normalizeContextFieldDefinitions(props.context?.custom_field_definitions)
    const next = { ...current }
    delete next[removedKey]
    definitionsOverride.value = next

    showRemoveConfirm.value = false
    fieldToRemove.value = null
    emit('saved', getApiMessage(response, 'Field deleted successfully.'))
  } finally {
    removingField.value = false
  }
}

async function saveFieldDefinition() {
  const label = String(fieldForm.value.label ?? '').trim()
  const type = String(fieldForm.value.type ?? 'TEXT').toUpperCase()
  const options = String(fieldForm.value.options ?? '')
    .split(',')
    .map((option) => option.trim())
    .filter(Boolean)

  if (!label) return
  if (type === 'DROPDOWN' && !options.length) {
    emit('error', 'Dropdown fields need at least one option.')
    return
  }

  const rows = [{ label, type, ...(type === 'DROPDOWN' ? { options } : {}) }]
  const saved = await submitFieldRows(rows, 'Field saved successfully.')
  if (saved) resetFieldForm()
}

watch(
  () => props.context?.context_id,
  () => {
    definitionsOverride.value = null
    resetFieldForm()
    showRemoveConfirm.value = false
    fieldToRemove.value = null
  },
)
</script>

<style scoped>
.ctx-tab {
  max-width: 42rem;
}
</style>
